import React, { useState, useEffect, useCallback } from 'react';
import { CandidateInterface } from 'components/models/CandidateInterfaces';
import { CompetenciasInterface } from 'components/models/SoftSkillsInterfaces';
import TextEditor from '../../../ui/textEditor';
import { Stack, Button, TextField, Typography, Box, IconButton, Tooltip } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SwalAlert from 'components/ui/alerts/SwalAlert';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

interface Props {
  dataCandidate: CandidateInterface;
  onTogglePrivado: (nombreCompetencia: string) => void;
  onChangeCompetencias: (competencias: Competencia[]) => void;
}

interface Competencia extends CompetenciasInterface {}

const SoftSkillsComponent = ({ dataCandidate, onTogglePrivado, onChangeCompetencias }: Props) => {
  
  const handleOnChangeInput =
    (name = '') =>
    (value: string) => {
      const nuevasCompetencias = dataCandidate.competencias?.map((data: Competencia) => {
        if (name === data.nombre && !Array.isArray(data.valor)) {
          return { ...data, valor: value };
        }
        return data;
      }) || [];
      onChangeCompetencias(nuevasCompetencias);
    };

  const handleArrayInputs = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nombre = event.target.getAttribute('name');
    const position = event.target.getAttribute('position');
    const valor = event.target.value;

    if (!nombre || position === null) return;

    const nuevasCompetencias = dataCandidate.competencias?.map(comp => {
      if (comp.nombre === nombre && Array.isArray(comp.valor)) {
        const newValor = [...comp.valor];
        newValor[parseInt(position)] = valor;
        return { ...comp, valor: newValor };
      }
      return comp;
    }) || [];
    onChangeCompetencias(nuevasCompetencias);
  };

  const handleAddCompetencias = (newCompetencia: Competencia) => {
    const nuevasCompetencias = [...(dataCandidate.competencias || []), newCompetencia];
    onChangeCompetencias(nuevasCompetencias);
  };

  const callFormAddCompetencia = () => {
    SwalAlert({
      title: 'Agregar Competencia',
      text: 'Ingrese el nombre de la competencia',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      input: 'text',
      showConfirmButton: true,
      confirmButtonText: 'Definir tipo de ingreso',
      inputValidator: (value: string) => {
        if (value === '') {
          return 'Ingrese el nombre de la competencia';
        }
      },
    }).then(({ isConfirmed, value: nombre }: any) => {
      if (isConfirmed && nombre) {
        SwalAlert({
          title: 'Agregar Competencia',
          text: 'Seleccione el tipo de ingreso',
          input: 'radio',
          inputOptions: { simple: 'Valor simple', multiple: 'Valor multiple' },
          showConfirmButton: true,
          confirmButtonText: 'Siguiente',
          showCancelButton: true,
          cancelButtonText: 'Cancelar',
        }).then(({ isConfirmed, value: type }: any) => {
          if (isConfirmed && type) {
            SwalAlert({
              title: 'Agregar Competencia',
              text:
                type === 'simple'
                  ? 'Ingrese el valor de la competencia'
                  : 'Ingrese un valor para su lista de competencia',
              input: 'text',
              confirmButtonText: 'Crear competencia',
              showConfirmButton: true,
              showCancelButton: true,
              cancelButtonText: 'Cancelar',
              background: '#213456',
            }).then(({ isConfirmed, value }: any) => {
              if (isConfirmed) {
                const nuevaCompetencia: Competencia = {
                  nombre: nombre,
                  valor: type === 'simple' ? value : [value || ''],
                  privado: false,
                };
                handleAddCompetencias(nuevaCompetencia);
              }
            });
          }
        });
      }
    });
  };

  const handleAddInput = (nombreCompetencia: string) => {
    const nuevasCompetencias = dataCandidate.competencias?.map(comp => {
      if (comp.nombre === nombreCompetencia && Array.isArray(comp.valor)) {
        return { ...comp, valor: [...comp.valor, ''] };
      }
      return comp;
    }) || [];
    onChangeCompetencias(nuevasCompetencias);
  };

  const returnListSkillTsx = (items: Competencia, index: number) => (
    <Stack key={items.nombre || index} direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
      {items.privado !== undefined && (
        <Tooltip title={items.privado ? "Marcar como público" : "Marcar como privado"}>
          <span>
            <IconButton
              size="small"
              onClick={() => items.nombre && onTogglePrivado(items.nombre)}
              disabled={!items.nombre}
              sx={{ cursor: items.nombre ? 'pointer' : 'default', mt: 4 }}
            >
              {items.privado ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Box sx={{ width: '100%' }}>
        <Typography component='label' variant='subtitle1' gutterBottom sx={{ display: 'block', mb: 1 }}>
          {items.nombre}
        </Typography>
        {Array.isArray(items.valor) ? (
          <Stack gap={1} alignItems='flex-start'>
            {items.valor.map((itemValor: string, key: number) => (
              <TextField
                key={key}
                name={items.nombre}
                variant='outlined'
                size='small'
                spellCheck={true}
                defaultValue={itemValor}
                onChange={handleArrayInputs}
                fullWidth
                inputProps={{ position: key.toString() }}
              />
            ))}
            {(items.nombre === 'Fortalezas' || items.nombre === 'Debilidades' || items.nombre === 'Hobbies') && (
              <Button onClick={() => items.nombre && handleAddInput(items.nombre)} startIcon={<AddCircleIcon />} size="small">
                Agregar
              </Button>
            )}
          </Stack>
        ) : (
          <TextEditor
            value={typeof items.valor === 'string' ? items.valor : ''}
            onChange={handleOnChangeInput(items.nombre)}
            placeholder={`Escribe sobre ${items.nombre}...`}
          />
        )}
      </Box>
    </Stack>
  );

  return (
    <Stack gap={3}>
      {dataCandidate.competencias && dataCandidate.competencias.length > 0 ? (
        dataCandidate.competencias.map((competencia: Competencia, index: number) =>
          returnListSkillTsx(competencia, index)
        )
      ) : (
        <Typography>No hay competencias definidas.</Typography>
      )}
      <Button
        onClick={callFormAddCompetencia}
        startIcon={<AddCircleIcon />}
        sx={{ alignSelf: 'flex-start', mt: 2 }}
      >
        Agregar competencia personalizada
      </Button>
    </Stack>
  );
};

export default SoftSkillsComponent;
